import { VALID_VISIBILITIES } from '../../../domain/posts/constants';
import { getActivityTitleServerSide } from '../../../shared/utils/activity/getActivityTitle';

export class InvalidPostDataError extends Error {
  constructor(field, message) {
    super(message);
    this.name = 'InvalidPostDataError';
    this.field = field;
  }
}

export default class CreatePost {
  constructor(postRepository) {
    if (!postRepository) {
      throw new Error('Post repository is required');
    }
    this.postRepository = postRepository;
  }

  async execute(postData, userId) {
    const validationError = this.validatePostData(postData);
    if (validationError) {
      throw new InvalidPostDataError(validationError.field, validationError.message);
    }

    if (!postData.author || !postData.author.uid) {
      throw new InvalidPostDataError('author', 'Author information is required');
    }

    const post = {
      ...postData,
      createdAt: new Date(),
      activity: postData.activity ? {
        type: typeof postData.activity.type === 'object' ? postData.activity.type.id : postData.activity.type,
        typeLabels: postData.activity.typeLabels || postData.activity.type?.labels || null,
        typeIcon: postData.activity.typeIcon || postData.activity.type?.icon || null,
        subtype: postData.activity.subtype || null,
        subtypeLabels: postData.activity.subtypeLabels || null,
        date: postData.activity.date || null,
        location: postData.activity.location || null,
        maxParticipants: postData.activity.maxParticipants || null,
        currentParticipants: 1,
        participants: [userId],
        title: getActivityTitleServerSide({
          typeLabels: postData.activity.typeLabels || postData.activity.type?.labels,
          subtype: postData.activity.subtype,
          subtypeLabels: postData.activity.subtypeLabels
        })
      } : null,
      participants: [userId],
      saves: [],
      shares: []
    };

    return this.postRepository.create(post);
  }

  validatePostData(data) {
    if (!data) {
      return { field: 'data', message: 'Post data is required' };
    }

    // Validation du contenu
    if (!data.content || typeof data.content !== 'string' || data.content.trim() === '') {
      return { field: 'content', message: 'Content is required and must be a non-empty string' };
    }

    // Validation de la visibilité si fournie
    if (data.visibility && !VALID_VISIBILITIES.includes(data.visibility)) {
      return { field: 'visibility', message: `Visibility must be one of: ${VALID_VISIBILITIES.join(', ')}` };
    }

    // Si une activité est fournie, valider ses champs
    if (data.activity) {
      return this.validateActivityData(data.activity);
    }

    return null;
  }

  validateActivityData(activity) {
    // Validation du type d'activité
    if (!activity.type) {
      return { field: 'activity.type', message: 'Activity type is required' };
    }
    
    // Si c'est une chaîne (ancien format)
    if (typeof activity.type === 'string') {
      if (activity.type.trim() === '') {
        return { field: 'activity.type', message: 'Activity type cannot be empty' };
      }
    } 
    // Si c'est un objet (nouveau format)
    else if (typeof activity.type === 'object') {
      if (!activity.type.id || typeof activity.type.id !== 'string' || activity.type.id.trim() === '') {
        return { field: 'activity.type.id', message: 'Activity type ID is required and must be a non-empty string' };
      }
      if (!activity.type.labels || typeof activity.type.labels !== 'object') {
        return { field: 'activity.type.labels', message: 'Activity type labels must be an object' };
      }
      if (!activity.type.icon || typeof activity.type.icon !== 'string') {
        return { field: 'activity.type.icon', message: 'Activity type icon must be a string' };
      }
    } else {
      return { field: 'activity.type', message: 'Activity type must be a string or an object' };
    }

    // Validation de la date
    if (!activity.date) {
      return { field: 'activity.date', message: 'Activity date is required' };
    }
    
    let validDate;
    if (activity.date instanceof Date) {
      validDate = activity.date;
    } else if (activity.date?.toDate) {
      validDate = activity.date.toDate();
    } else if (typeof activity.date === 'string' && activity.date.trim() !== '') {
      validDate = new Date(activity.date);
    }

    if (validDate && isNaN(validDate.getTime())) {
      return { field: 'activity.date', message: 'Activity date must be a valid date' };
    }

    // Validation de la localisation
    if (activity.location) {
      if (typeof activity.location !== 'object') {
        return { field: 'activity.location', message: 'Location must be an object' };
      }
      if (!activity.location.address || typeof activity.location.address !== 'string' || activity.location.address.trim() === '') {
        return { field: 'activity.location.address', message: 'Location address is required and must be a non-empty string' };
      }
      if (activity.location.placeId && typeof activity.location.placeId !== 'string') {
        return { field: 'activity.location.placeId', message: 'Location placeId must be a string' };
      }
      if (activity.location.location && typeof activity.location.location !== 'object') {
        return { field: 'activity.location.location', message: 'Location location must be an object' };
      }
      if (activity.location.location && (
        typeof activity.location.location.lat !== 'number' || 
        typeof activity.location.location.lng !== 'number'
      )) {
        return { field: 'activity.location.coordinates', message: 'Location coordinates must be valid numbers' };
      }
    }

    // Validation du nombre de participants
    if (activity.maxParticipants && (!Number.isInteger(activity.maxParticipants) || activity.maxParticipants <= 0)) {
      return { field: 'activity.maxParticipants', message: 'Activity maxParticipants must be a positive integer' };
    }

    // Validation de la sous-catégorie si présente
    if (activity.subtype !== undefined && activity.subtype !== null) {
      // Vérifier que la sous-catégorie est une chaîne non vide
      if (typeof activity.subtype !== 'string' || activity.subtype.trim() === '') {
        return { field: 'activity.subtype', message: 'Activity subtype must be a non-empty string' };
      }
      
      // Si on a une sous-catégorie, les labels sont obligatoires
      if (!activity.subtypeLabels || 
          typeof activity.subtypeLabels !== 'object' ||
          !activity.subtypeLabels.fr ||
          !activity.subtypeLabels.en) {
        return { field: 'activity.subtypeLabels', message: 'Activity subtypeLabels must include fr and en fields' };
      }
    }

    return null;
  }
} 